import React, { useEffect, useState } from 'react';
import { SubscriptionData, userSubscriptionDataService } from '../../services/UserSubscriptionDataService';
import { Card } from '../ui/card';

const UserSubscriptionCard: React.FC = () => {
  const [subscriptionData, setSubscriptionData] = useState<SubscriptionData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await userSubscriptionDataService.refreshSubscriptionData();
      setLoading(false);
    };
    
    fetchData();
    
    // Subscribe to future updates
    const unsubscribe = userSubscriptionDataService.subscribe((data) => {
      setSubscriptionData(data);
    });
    
    return () => unsubscribe();
  }, []);
  
  // Format date for display
  const formatDate = (date: Date | null): string => {
    if (!date) return 'N/A';
    
    // Get the user's browser locale
    const userLocale = navigator.language || 'en-US';
    
    // Format date according to the user's locale
    return date.toLocaleDateString(userLocale, { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };
  
  const getSubscriptionStatusClass = (isActive: boolean): string => {
    return isActive 
      ? 'bg-success/10 text-success' 
      : 'bg-warning/10 text-warning';
  };
  
  //console.log('subscriptionData', subscriptionData);
  
  // Handle loading state
  if (loading && !subscriptionData) {
    return (
      <Card className="p-5 h-full">
        <div className="flex justify-center items-center h-32">
          <div className="animate-pulse flex space-x-4">
            <div className="flex-1 space-y-4 py-1">
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
              <div className="space-y-2">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-5/6"></div>
              </div>
            </div>
          </div>
        </div>
      </Card>
    );
  }
  
  return (
    <Card className="p-5 h-full">
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Subscription Status</h3>
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getSubscriptionStatusClass(subscriptionData?.is_active || false)}`}>
            {subscriptionData?.is_active ? 'Active' : 'None'}
          </span>
        </div>
        
        <div className="mt-5 grid gap-5">
          <div className="flex items-center justify-between">
            <p className="text-gray-500 dark:text-gray-400">Plan</p>
            <p className="font-medium text-gray-900 dark:text-white">{subscriptionData?.tier_code || 'None'}</p>
          </div>
          
          <div className="flex items-center justify-between">
            <p className="text-gray-500 dark:text-gray-400">Period Start</p>
            <p className="font-medium text-gray-900 dark:text-white">{formatDate(subscriptionData?.start_date ? subscriptionData?.start_date : null)}</p>
          </div>
          
          <div className="flex items-center justify-between">
            <p className="text-gray-500 dark:text-gray-400">Period End</p>
            <p className="font-medium text-gray-900 dark:text-white">{formatDate(subscriptionData?.end_date ? subscriptionData?.end_date : null)}</p>
          </div>
          
          {subscriptionData?.marked_for_cancel && (
            <div className="mt-2 p-2 bg-warning/10 rounded-md">
              <p className="text-warning text-sm">
                Your subscription will be canceled on {formatDate(subscriptionData?.cancel_at ? subscriptionData?.cancel_at : null)}
              </p>
            </div>
          )}
        </div>
        
        <div className="mt-auto pt-4">
          <a 
            href="/subscription" 
            className="text-brand-500 hover:text-brand-600 dark:text-brand-400 dark:hover:text-brand-300 text-sm font-medium"
          >
            Manage Subscription →
          </a>
        </div>
      </div>
    </Card>
  );
};

export default UserSubscriptionCard;